import { logger } from '@fe/core';
import React from 'react';
import app from '../../client/app';
import { localStorageService } from '../../client/local-storage-service';
import { WorkerClient } from '../../client/worker-client';
import { FileSystemExtendedAvailable } from '../../common/helpers';
import { envConfig } from '../../configs/env';
import { MessageType } from '../../services/actor-messages';
import { LoadingIndicatorLinear } from '../components/indicators/LoadingIndicatorLinear';
import { useAppSelector } from './store-hooks';
import { useSafeEffect } from './use-safe-effect';

interface WorkerContext {
    worker: WorkerClient | null;
}
const WorkerContext = React.createContext<WorkerContext>({
    worker: null
});

export function FlightWorkerProvider(props: { children?: React.ReactNode }) {
    const user = useAppSelector((x) => x.user.info);
    const [worker, setWorker] = React.useState<WorkerClient | null>(null);
    const integrationId = useAppSelector((x) => x.settings.integrationId);

    useSafeEffect(() => {
        if (!user.isAuthenticated) {
            return;
        }

        (async () => {
            logger.info('Starting flight worker');

            const worker = await app.initWorker();
            await worker.actor.sendAsync({
                type: MessageType.serviceStart,
                data: {
                    url: envConfig.ws,
                    altUrl: envConfig.wsAlt,
                    userId: user.name,
                    password: localStorageService.getWsPasscode()!,
                    fpmId: integrationId || undefined,
                    supportsFileApiExtended: FileSystemExtendedAvailable
                }
            });
            setWorker(worker);
        })();
        return () => {
            logger.info('Stopping flight worker');
            app.worker.actor.sendAsync({ type: MessageType.serviceStop });
            app.disposeWorker();
            setWorker(null);
        };
    }, [user]);

    const contextValue = React.useMemo(() => ({ worker }), [worker]);

    if (!worker) {
        return (
            <div>
                <LoadingIndicatorLinear />
                {import.meta.env.DEV && <div>Initializing flight worker...</div>}
            </div>
        );
    }

    return <WorkerContext.Provider value={contextValue}>{props.children}</WorkerContext.Provider>;
}

// TODO: replace all references to app.worker.actor with this hook
// TODO: make a wrapper that allows multiple subscribers to the same event, this can only work for messages that return void so need to play around with TS a bit to do that
export function useFlightWorker() {
    const worker = React.useContext(WorkerContext).worker;
    if (!worker) {
        throw new Error('Worker uninitialized');
    }
    return worker;
}
